import { __extends } from "tslib";
import { DiffuseCompositeMethod } from './DiffuseCompositeMethod';
/**
 * DiffuseLightMapMethod provides a diffuse shading method that uses a light map to modulate the calculated diffuse
 * lighting. It is different from EffectLightMapMethod in that the latter modulates the entire calculated pixel color, rather
 * than only the diffuse lighting value.
 */
var DiffuseLightMapMethod = /** @class */ (function (_super) {
    __extends(DiffuseLightMapMethod, _super);
    /**
     * Creates a new DiffuseLightMapMethod method.
     *
     * @param lightMap The texture containing the light map.
     * @param blendMode The blend mode with which the light map should be applied to the lighting result.
     * @param useSecondaryUV Indicates whether the secondary UV set should be used to map the light map.
     * @param baseMethod The diffuse method used to calculate the regular diffuse-based lighting.
     */
    function DiffuseLightMapMethod(lightMap, blendMode, useSecondaryUV, baseMethod) {
        if (blendMode === void 0) { blendMode = 'multiply'; }
        if (useSecondaryUV === void 0) { useSecondaryUV = false; }
        if (baseMethod === void 0) { baseMethod = null; }
        var _this = _super.call(this, baseMethod) || this;
        _this._lightMap = lightMap;
        _this.blendMode = blendMode;
        _this._useSecondaryUV = useSecondaryUV;
        if (_this._lightMap)
            _this.iAddTexture(_this._lightMap);
        return _this;
    }
    Object.defineProperty(DiffuseLightMapMethod.prototype, "assetType", {
        /**
         * @inheritDoc
         */
        get: function () {
            return DiffuseLightMapMethod.assetType;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DiffuseLightMapMethod.prototype, "blendMode", {
        /**
         * The blend mode with which the light map should be applied to the lighting result.
         *
         * @see DiffuseLightMapMethod.ADD
         * @see DiffuseLightMapMethod.MULTIPLY
         */
        get: function () {
            return this._blendMode;
        },
        set: function (value) {
            if (value != DiffuseLightMapMethod.ADD && value != DiffuseLightMapMethod.MULTIPLY)
                throw new Error('Unknown blendmode!');
            if (this._blendMode == value)
                return;
            this._blendMode = value;
            this.invalidateShaderProgram();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DiffuseLightMapMethod.prototype, "lightMap", {
        /**
         * The texture containing the light map data.
         */
        get: function () {
            return this._lightMap;
        },
        set: function (value) {
            if (this._lightMap == value)
                return;
            if (this._lightMap)
                this.iRemoveTexture(this._lightMap);
            this._lightMap = value;
            if (this._lightMap)
                this.iAddTexture(this._lightMap);
            this.invalidateShaderProgram();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DiffuseLightMapMethod.prototype, "useSecondaryUV", {
        /**
         * Indicates whether the secondary UV set should be used to map the light map.
         */
        get: function () {
            return this._useSecondaryUV;
        },
        set: function (value) {
            if (this._useSecondaryUV == value)
                return;
            this._useSecondaryUV = value;
            this.invalidateShaderProgram();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Indicates the light map should be multiplied with the calculated shading result.
     * This can be used to add pre-calculated shadows or occlusion.
     */
    DiffuseLightMapMethod.MULTIPLY = 'multiply';
    /**
     * Indicates the light map should be added into the calculated shading result.
     * This can be used to add pre-calculated lighting or global illumination.
     */
    DiffuseLightMapMethod.ADD = 'add';
    DiffuseLightMapMethod.assetType = '[asset DiffuseLightMapMethod]';
    return DiffuseLightMapMethod;
}(DiffuseCompositeMethod));
export { DiffuseLightMapMethod };
import { ShaderBase, ImageTexture2D } from '@awayjs/renderer';
import { _Shader_LightingCompositeMethod } from './CompositeMethodBase';
/**
 * _Shader_DiffuseLightMapMethod provides a diffuse shading method that uses a light map to modulate the calculated diffuse
 * lighting. It is different from EffectLightMapMethod in that the latter modulates the entire calculated pixel color, rather
 * than only the diffuse lighting value.
 */
var _Shader_DiffuseLightMapMethod = /** @class */ (function (_super) {
    __extends(_Shader_DiffuseLightMapMethod, _super);
    /**
     * Creates a new _Shader_DiffuseLightMapMethod method.
     */
    function _Shader_DiffuseLightMapMethod(method, shader) {
        var _this = _super.call(this, method, shader) || this;
        _this._method = method;
        _this._shader = shader;
        return _this;
    }
    /**
     * @inheritDoc
     */
    _Shader_DiffuseLightMapMethod.prototype._initVO = function (chunkVO) {
        var texture = this._method.lightMap || new ImageTexture2D();
        this._lightMap = texture.getAbstraction(this._shader);
        this._lightMap._initVO(chunkVO);
        if (this._method.useSecondaryUV)
            this._shader.secondaryUVDependencies++;
        else
            this._shader.uvDependencies++;
    };
    /**
     * @inheritDoc
     */
    _Shader_DiffuseLightMapMethod.prototype._initConstants = function () {
        this._lightMap._initConstants();
    };
    /**
     * @inheritDoc
     */
    _Shader_DiffuseLightMapMethod.prototype._getFragmentCode = function (targetReg, registerCache, sharedRegisters) {
        var code;
        var temp = registerCache.getFreeFragmentVectorTemp();
        code = this._lightMap._getFragmentCode(temp, registerCache, sharedRegisters, this._method.useSecondaryUV ? sharedRegisters.secondaryUVVarying : sharedRegisters.uvVarying);
        switch (this._method.blendMode) {
            case DiffuseLightMapMethod.MULTIPLY:
                code += 'mul ' + this._totalLightColorReg + ', ' + this._totalLightColorReg + ', ' + temp + '\n';
                break;
            case DiffuseLightMapMethod.ADD:
                code += 'add ' + this._totalLightColorReg + ', ' + this._totalLightColorReg + ', ' + temp + '\n';
                break;
        }
        code += _super.prototype._getFragmentCode.call(this, targetReg, registerCache, sharedRegisters);
        return code;
    };
    /**
     * @inheritDoc
     */
    _Shader_DiffuseLightMapMethod.prototype._activate = function () {
        _super.prototype._activate.call(this);
        this._lightMap.activate();
    };
    /**
     * @inheritDoc
     */
    _Shader_DiffuseLightMapMethod.prototype._setRenderState = function (renderState) {
        _super.prototype._setRenderState.call(this, renderState);
        this._lightMap._setRenderState(renderState);
    };
    return _Shader_DiffuseLightMapMethod;
}(_Shader_LightingCompositeMethod));
export { _Shader_DiffuseLightMapMethod };
ShaderBase.registerAbstraction(_Shader_DiffuseLightMapMethod, DiffuseLightMapMethod);
